import { cn } from '@/lib/cn'
import {
  Button,
  SelectContent,
  SelectItem,
  SelectRoot,
  SelectTrigger,
  SelectValue,
} from '@/components/ui'
import type { TablePaginationBarProps } from './types/table-pagination-bar.types'

export type { TablePaginationBarProps } from './types/table-pagination-bar.types'

/**
 * Shared pagination footer for list tables.
 *
 * Renders "Showing X–Y of Z" range text, optional page-size selector,
 * and Previous / Next navigation. Hides itself when there is nothing to
 * paginate (total === 0 or totalPages <= 1 with no leading/trailing slots).
 */
export function TablePaginationBar({
  page,
  limit,
  total,
  totalPages,
  onPageChange,
  leadingContent,
  trailingContent,
  pageSizeOptions,
  onLimitChange,
  className,
}: TablePaginationBarProps): React.ReactElement | null {
  if (total === 0 && !leadingContent && !trailingContent) return null

  const start = total > 0 ? (page - 1) * limit + 1 : 0
  const end = Math.min(page * limit, total)
  const showNav = totalPages > 1

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-3',
        className,
      )}
    >
      <div className="flex flex-wrap items-center gap-3">
        {leadingContent}
        {total > 0 && (
          <p className="text-xs text-gray-600 dark:text-gray-400">
            Showing {start}–{end} of {total}
          </p>
        )}
      </div>

      <div className="flex items-center gap-2">
        {trailingContent}

        {pageSizeOptions && onLimitChange && (
          <SelectRoot
            value={String(limit)}
            onValueChange={(value) => onLimitChange(Number(value))}
          >
            <SelectTrigger
              aria-label="Rows per page"
              className="h-9 w-[116px] rounded-lg text-xs font-medium"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent align="end">
              {pageSizeOptions.map((size) => (
                <SelectItem key={size} value={String(size)}>
                  {size} / page
                </SelectItem>
              ))}
            </SelectContent>
          </SelectRoot>
        )}

        {showNav && (
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onPageChange(page - 1)}
              disabled={page <= 1}
            >
              Previous
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={() => onPageChange(page + 1)}
              disabled={page >= totalPages}
            >
              Next
            </Button>
          </>
        )}
      </div>
    </div>
  )
}

TablePaginationBar.displayName = 'TablePaginationBar'
